import { useMemo, useState } from "react";
import { api, ApiError } from "../../lib/api-client";
import { ALL_CAMPAIGNS, type AimfoxCampaign, type CampaignNotificationPref, type MessageType, type NotificationRecipient } from "../../lib/types";
import { Button } from "../ui/button";
import { Input } from "../ui/input";
import { Icon } from "../ui/icon";
import { CampaignMultiSelect } from "./campaign-multi-select";
import { CampaignPrefRow } from "./campaign-pref-row";

interface RecipientFormProps {
  mode: "create" | "edit";
  recipient?: NotificationRecipient;
  campaigns: AimfoxCampaign[];
  /** True until the campaign picker's own (independent, non-blocking) fetch
   * resolves — lets the picker say "loading" instead of "everything's
   * already added" while campaigns is still just an empty placeholder. */
  campaignsLoading?: boolean;
  onSaved: (recipient: NotificationRecipient) => void;
  onDeleted: () => void;
  onCancel: () => void;
}

// Add AND edit share this one form (per spec: clicking a listed email opens
// "that email information" pre-filled, same shape as adding one) — the only
// difference is whether `recipient` is passed and whether the email field is
// editable. The backend's update route only replaces campaign prefs, not the
// email itself (email is the row's unique identity) — so in edit mode the
// address is shown, not an input; changing it means adding a new recipient
// and removing the old one.
export function RecipientForm({ mode, recipient, campaigns, campaignsLoading, onSaved, onDeleted, onCancel }: RecipientFormProps) {
  const [email, setEmail] = useState(recipient?.email ?? "");
  const [prefs, setPrefs] = useState<CampaignNotificationPref[]>(recipient?.campaignPrefs ?? []);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [confirmDelete, setConfirmDelete] = useState(false);

  const campaignsById = useMemo(() => new Map(campaigns.map((c) => [c.id, c])), [campaigns]);
  const activeCampaigns = useMemo(() => campaigns.filter((c) => c.state === "ACTIVE"), [campaigns]);

  function campaignName(campaignId: string): string {
    if (campaignId === ALL_CAMPAIGNS) return "All campaigns";
    return campaignsById.get(campaignId)?.name ?? campaignId;
  }

  function addCampaign(campaignId: string) {
    if (!campaignId || prefs.some((p) => p.campaignId === campaignId)) return;
    setPrefs([...prefs, { campaignId, messageTypes: [] }]);
  }

  function updateCampaignTypes(campaignId: string, messageTypes: MessageType[]) {
    setPrefs(prefs.map((p) => (p.campaignId === campaignId ? { ...p, messageTypes } : p)));
  }

  function removeCampaign(campaignId: string) {
    setPrefs(prefs.filter((p) => p.campaignId !== campaignId));
  }

  // Mandatory per the spec: at least one campaign, and at least one message
  // type on every campaign row — checked client-side for instant feedback,
  // and again by the backend (NotificationSettingsInputError, plus the
  // schema's own CHECK(cardinality(message_types) > 0)) as the backstop.
  const emailValid = mode === "edit" || /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email.trim());
  const hasEmptyRow = prefs.some((p) => p.messageTypes.length === 0);
  const canSave = emailValid && prefs.length > 0 && !hasEmptyRow;

  async function handleSave() {
    if (!canSave) return;
    setBusy(true);
    setError(null);
    try {
      const { recipient: saved } =
        mode === "create"
          ? await api.createNotificationRecipient(email.trim(), prefs)
          : await api.updateNotificationRecipient(recipient!.id, prefs);
      onSaved(saved);
    } catch (e) {
      setError(e instanceof ApiError ? e.message : e instanceof Error ? e.message : String(e));
    } finally {
      setBusy(false);
    }
  }

  async function handleDelete() {
    if (!recipient) return;
    setBusy(true);
    setError(null);
    try {
      await api.deleteNotificationRecipient(recipient.id);
      onDeleted();
    } catch (e) {
      setError(e instanceof ApiError ? e.message : e instanceof Error ? e.message : String(e));
      setBusy(false);
    }
  }

  return (
    <div className="space-y-spacing-lg">
      <div className="space-y-spacing-3xs">
        <label htmlFor="notif-email" className="font-label-sm text-label-sm text-on-surface-variant uppercase tracking-wide">
          Email address
        </label>
        {mode === "create" ? (
          <Input
            id="notif-email"
            type="email"
            placeholder="name@company.com"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            className="w-full"
          />
        ) : (
          <p id="notif-email" className="font-body-md text-body-md text-on-surface">{email}</p>
        )}
      </div>

      <div className="space-y-spacing-sm">
        <label className="font-label-sm text-label-sm text-on-surface-variant uppercase tracking-wide">
          Campaigns to notify on
        </label>
        <CampaignMultiSelect
          activeCampaigns={activeCampaigns}
          alreadySelected={prefs.map((p) => p.campaignId)}
          onAdd={addCampaign}
          loading={campaignsLoading}
        />
        {prefs.length === 0 ? (
          <p className="font-body-sm text-body-sm text-on-surface-variant">
            No campaigns added yet — add at least one above.
          </p>
        ) : (
          <div className="space-y-spacing-xs">
            {prefs.map((p) => (
              <CampaignPrefRow
                key={p.campaignId}
                pref={p}
                campaignName={campaignName(p.campaignId)}
                campaignIsActive={campaignsById.get(p.campaignId)?.state === "ACTIVE"}
                showRequiredError={p.messageTypes.length === 0}
                onChange={(types) => updateCampaignTypes(p.campaignId, types)}
                onRemove={() => removeCampaign(p.campaignId)}
              />
            ))}
          </div>
        )}
      </div>

      {error && (
        <p className="font-label-sm text-label-sm text-error" role="alert">
          {error}
        </p>
      )}

      <div className="flex items-center justify-between gap-spacing-sm pt-spacing-xs">
        {mode === "edit" ? (
          confirmDelete ? (
            <div className="flex items-center gap-spacing-xs">
              <span className="font-label-sm text-label-sm text-on-surface-variant">Remove this recipient?</span>
              <Button variant="danger" disabled={busy} onClick={() => void handleDelete()}>
                Yes, remove
              </Button>
              <Button variant="ghost" disabled={busy} onClick={() => setConfirmDelete(false)}>
                Cancel
              </Button>
            </div>
          ) : (
            <Button variant="danger" disabled={busy} onClick={() => setConfirmDelete(true)}>
              Delete email
            </Button>
          )
        ) : (
          <span />
        )}
        <div className="flex items-center gap-spacing-xs">
          <Button variant="ghost" disabled={busy} onClick={onCancel}>
            Cancel
          </Button>
          <Button variant="primary" disabled={busy || !canSave} onClick={() => void handleSave()}>
            {busy && <Icon name="progress_activity" className="text-[16px] animate-spin" />}
            {mode === "create" ? "Save email" : "Save changes"}
          </Button>
        </div>
      </div>
    </div>
  );
}
